import { useState, useEffect } from "react";
import { Button } from "rsuite";
import FilterComponent from "../../components/FilterComponent";
import upArrow from "../../assets/uparrowFilter.svg";
import downArrow from "../../assets/downarrowFilter.svg";
import CustomLoader from "../../common/CustomLoader/CustomLoader";
import reload from "../../assets/reload.svg";
import { useGetFilterDataMutation, useGetCardsDataMutation, useGetGraphDataMutation } from "../../services/redux/api/inventoryOverview";
import Graph from "../../common/Graph";
import Card from "../../common/Card";
import { formatter } from "../../utils/formatter";
import InventoryMap from "../../components/ForecastOverview/inventoryMap";
import {
  setApplyFilter,
  setSelectedDBFilter,
  setMapDC
} from "../../services/redux/reducer/demandfilter.reducer";
import { useDispatch } from "react-redux";

const filterLabels: Record<string, string> = {
  dc: "DC",
  itemId: "Item ID",
  productDesc: "Product Desc",
  category: "Category",
  productSegm: "Segmentation",
  subCategory: "Sub Category",
  modelType: "Model Type",
  storeNumber: "Store Number",
  ucode: "UCode",
  vendorName: "Vendor",
};

interface SelectedFilters {
  [key: string]: string[];
}

// API response filter option structure
interface APIFilterOption {
  filterName: string;
  filterVal: string;
}

// Format needed for FilterComponent
interface ComponentFilterOption {
  [key: string]: string;
}

interface FilterAPIResponse {
  [key: string]: APIFilterOption[];
}

interface MarkerClickData {
  branchId: string;
  storeNumber: string;
  location: [number, number];
  forecast: number;
}

function InventoryOverview() {
  const [isVisible, setIsVisible] = useState(true);
  const [cardData, setCardData] = useState<any>([]);
  const [mapGraphData, setMapGraphData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    category: [],
    dc: [],
    itemId: [],
    productDesc: [],
    productSegm: [],
    storeNumber: [],
    subCategory: [],
    ucode: [],
    vendorName: [],
  });
  
  const [filterData, setFilterData] = useState<FilterAPIResponse>({
    category: [],
    dc: [],
    itemId: [],
    productDesc: [],
    productSegm: [],
    storeNumber: [],
    subCategory: [],
    ucode: [],
    vendorName: [],
  });
  const [appliedFilters, setAppliedFilters] = useState({});

  const [getFilterData, { isLoading: isFilterLoading }] = useGetFilterDataMutation();
  const [getCardData, { isLoading: isCardLoading }] = useGetCardsDataMutation();
  const [getMapGraphData, { isLoading: isMapLoading }] = useGetGraphDataMutation();
  const dispatch = useDispatch();

  const buildFiltersPayload = () => {
    return {
      category: selectedFilters.category || [],
      dc: selectedFilters.dc || [],
      itemId: selectedFilters.itemId || [],
      productDesc: selectedFilters.productDesc || [],
      productSegm: selectedFilters.productSegm || [],
      storeNumber: selectedFilters.storeNumber || [],
      subCategory: selectedFilters.subCategory || [],
      ucode: selectedFilters.ucode || [],
      vendorName: selectedFilters.vendorName || [],
    };
  };

  const fetchFilterData = async (markerData?: MarkerClickData) => {
    try {
      setIsLoading(true);
      const payload = buildFiltersPayload(); 
      if (markerData?.branchId) {
        payload.dc = [markerData.branchId];
      }
      const response = await getFilterData(payload as any).unwrap();
      if (response?.data) {
        setFilterData(response.data);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCardData = async (markerData?: MarkerClickData) => {
    try {
      setIsLoading(true);
      const payload = buildFiltersPayload(); 
      if (markerData?.branchId) {
        payload.dc = [markerData.branchId];
      }
      const response = await getCardData(payload as any).unwrap();
      setCardData(response?.data);
    } catch (error) {
      console.error("Error fetching card data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGraphData = async (markerData?: MarkerClickData) => {
    try {
      setIsLoading(true);
      const payload = buildFiltersPayload(); 
      if (markerData?.branchId) {
        payload.dc = [markerData.branchId];
      }
      const response = await getMapGraphData(payload as any).unwrap();
      setMapGraphData(response?.chart1);
    } catch (error) {
      console.error("Error fetching graph data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (filterKey: string, values: string[]) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterKey]: values
    }));
  };

  const handleApplyFilters = () => {
    dispatch(setSelectedDBFilter(selectedFilters));
    setAppliedFilters(selectedFilters);
    dispatch(setApplyFilter("applied"));
    fetchFilterData();
    fetchCardData();
    fetchGraphData();
  };

  const handleClearFilters = () => {
    setAppliedFilters({});
    setSelectedFilters({
      category: [],
      dc: [],
      itemId: [],
      productDesc: [],
      productSegm: [],
      storeNumber: [],
      subCategory: [],
      ucode: [],
      vendorName: [],
    });
    fetchFilterData();
    fetchCardData();
    fetchGraphData();
    dispatch(setApplyFilter(""));
    dispatch(setSelectedDBFilter({ dc: []}));
    dispatch(setMapDC([]));
  };

  const filterShowHandler = () => {
    setIsVisible((prev) => !prev);
  };

  const handleMarkerClick = (markerData: MarkerClickData) => {
    fetchFilterData(markerData);
    fetchCardData(markerData);
    fetchGraphData(markerData);
  };

  useEffect(() => {
    fetchFilterData();
    fetchCardData();
    fetchGraphData();
  }, []); 

  const mapOptionsForComponent = (options: APIFilterOption[]): ComponentFilterOption[] => {
    return options.map(opt => ({
      filterVal: opt.filterVal,
      filterName: opt.filterName
    }));
  };  

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {isLoading && <CustomLoader open={true} />}
      <div className="relative">
        <div
          className={`flex flex-col gap-1 px-[2%] border-b-[1px] shadow ${
            !isVisible ? "pt-[8px] pb-[2px]" : "pt-[8px] pb-[15px]"
          }`}
        >
          <div className="flex justify-between items-center mb-1">
            <h3 className="page-title font-semibold">{"Inventory & Replenishment > Inventory Overview"}</h3>
            {
              <div className="flex gap-2">
                <img src={reload} alt="reload" className="w-[1vw]" />
                <p className="text-fs16 text-[#858585] font-semibold">
                  Last Updated : 02/27/2024
                </p>
              </div>
            }
          </div>

          {/* Filters Section */}
          {isVisible && (
            <div className="grid grid-cols-[1fr_auto] items-end gap-4">
              <div className="grid grid-cols-12 gap-2 items-end">
                {/* Render Filters */}
                {filterData &&
                  Object.entries(filterData)
                    .filter(([filterKey]) => !['adType', 'storeNumber', 'ucode', 'fcwNumber', 'modelType'].includes(filterKey))
                    .map(([filterKey, options]) => {
                      const optionsArray = Array.isArray(options) ? options : [];
                      return (
                      <FilterComponent
                        key={filterKey}
                        loading={isFilterLoading}
                        filterKey={filterKey}
                        filterName={
                          filterLabels[filterKey]
                            ? filterLabels[filterKey]
                            : filterKey
                                .replace(/_/g, " ")
                                .replace(
                                  /\w\S*/g,
                                  (w) =>
                                    w.charAt(0).toUpperCase() +
                                    w.substring(1).toLowerCase()
                                )
                        }
                        options={mapOptionsForComponent(optionsArray)}
                        labelKey="filterVal" 
                        valueKey="filterVal"
                        value={selectedFilters[filterKey] || []}
                        onFilterChange={handleFilterChange}
                        onDropdownOpen={() => {}}
                      />
                    );
                  })}
              </div>
              <div className="align-right flex gap-1">
                <Button
                  onClick={handleClearFilters}
                  className="bg-[#949494] text-white px-[20px] h-[4vh] rounded font-semibold btn-text"
                >
                  Clear
                </Button>

                <Button
                  onClick={handleApplyFilters}
                  className="bg-deloitteGreen text-white px-[20px] h-[4vh] rounded font-semibold btn-text"
                >
                  Apply
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-center absolute left-2/4 top-full translate-x-[-50%] translate-y-[-50%] cursor-pointer z-[10]" onClick={filterShowHandler}>
            {isVisible && (
              <img className="w-[1.5vw]" src={upArrow} alt="upArrow" />
            )}
            {!isVisible && (
              <img className="w-[1.5vw]" src={downArrow} alt="downArrow" />
            )}
          </div>
        </div>
      </div>

      {/* Content Area with flex-grow to fill available space */}
      <div className="bg-[#F1F1F1] flex-1 px-[2%] py-[10px] flex flex-col overflow-hidden">
        {/* Cards Section */}
        <div className="mb-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
        <Card 
          title="Current Inventory Details (02/27/2024)"
          leftValue={formatter(cardData[0]?.currentDayOnhandLb)}
          leftLabel="OnHand (Qty.)"
          rightValue={formatter(cardData[0]?.currentDayOnHandDollar)}
          rightLabel="OnHand ($)"
        />
        
        <Card 
          title="Previous Day Inventory Details (02/26/2024)"
          leftValue={formatter(cardData[0]?.prevdayOnhandLb)}
          leftDelta={formatter(cardData[0]?.prevdayOnhandLbDelta)}
          leftDirection="up"
          leftLabel="OnHand (Qty.)"
          rightValue={formatter(cardData[0]?.prevdayOnHandDollar)}
          rightDelta={formatter(cardData[0]?.prevdayOnHandDollarDelta)}
          rightDirection="down"
          rightLabel="OnHand ($)"
        />
        
        <Card 
          title="Previous Week Inventory Details (02/23/2024)"
          leftValue={formatter(cardData[0]?.weekOnhandLb)}
          leftDelta={formatter(cardData[0]?.weekOnhandLbDelta)}
          leftDirection="up"
          leftLabel="OnHand (Qty.)"
          rightValue={formatter(cardData[0]?.weekOnHandDollar)}
          rightDelta={formatter(cardData[0]?.weekOnHandDollarDelta)}
          rightDirection="up"
          rightLabel="OnHand ($)"
        />
        
        <Card 
          title="Previous Month Inventory Details (01/31/2024)"
          leftValue={formatter(cardData[0]?.monthOnhandLb)}
          leftDelta={formatter(cardData[0]?.monthOnhandLbDelta)}
          leftDirection="up"
          leftLabel="OnHand (Qty.)"
          rightValue={formatter(cardData[0]?.monthOnHandDollar)}
          rightDelta={formatter(cardData[0]?.monthOnHandDollarDelta)}
          rightDirection="up"
          rightLabel="OnHand ($)"
        />
        
        <Card 
          title="Current Date Open PO (02/27/2024)"
          leftValue={formatter(cardData[0]?.currentDayOpenERO)}
          leftLabel="Open PO (Qty.)"
        />
      </div>
        </div>

        {/* Map and Graph Section - flex-1 makes it take remaining space */}
    <div className="grid grid-cols-2 gap-3 h-[95%]"> 
    <div className="h-[97%]" > 
      <InventoryMap 
        title={"Current OnHand (Qty) Across Network"} 
        onMarkerClick={handleMarkerClick}
        filters={selectedFilters}
      />
    </div>

    
    <div className=" relative">
      <div className="h-[97%] ">
        <Graph
          title="Inventory Trend Last 2 Weeks"
          graphTypes={["bar", "line"]}
          data={mapGraphData}
          dataKey={["OpenERO", "OnhandLb"]}
          dataName={["Open PO (Qty.)", "OnHand (Qty.)"]}
          fill={["#BBED9B", "#027148"]}
          width={30}
          curveType={"linear"}
          xAxisKey={"InventoryDate"}
          margin={{ top: 15, right: 20, bottom: 10, left: 10 }} 
          isRequiredYAxisFormatter={false}
          isRequiredTickFormatter={true}
          isRequiredYaxisid={true}
          yAxisLabel="Open PO (Qty.)"
          yAxisLabelSecondAxis="OnHand (Qty.)"
        />
      </div>
    </div>
  </div>
      </div>
    </div>
  );
}

export default InventoryOverview;


